import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  UploadFile,
  EditNote,
  ArrowBack,
  Download,
  Help,
  Chat
} from '@mui/icons-material';
import { brand, bg } from '../../theme/colors';

const GetQuoteMethodSelector = ({ project, onSelectMethod, onBack }) => {
  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Get Material Quotes from Suppliers
        </Typography>
        {onBack && (
          <Button
            size="small"
            onClick={onBack}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        )}
      </Box>

      {project && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Project: <strong>{project.title}</strong>
        </Typography>
      )}

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Choose how you want to request quotes:
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* BOQ Upload Option */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-4px)'
              }
            }}
            onClick={() => onSelectMethod('boq_upload')}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <UploadFile sx={{ fontSize: 48, color: brand.primary, mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Upload Bill of Quantities (BOQ)
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Already have a detailed BOQ? Upload it and specify what you need.
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ mr: 1, color: 'success.main' }}>✓</Box>
                  Upload PDF, Excel, Word, or CSV file
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ mr: 1, color: 'success.main' }}>✓</Box>
                  Specify if you need everything or specific items
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ mr: 1, color: 'success.main' }}>✓</Box>
                  Add notes or special requirements
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ mr: 1, color: 'success.main' }}>✓</Box>
                  Faster for suppliers to quote accurately
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Best for: Clients with detailed material specifications
              </Typography>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<UploadFile />}
                sx={{
                  background: brand.gradient,
                  '&:hover': {
                    background: brand.gradientHover
                  }
                }}
              >
                Upload BOQ & Request Quotes
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Material Form Option */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-4px)'
              }
            }}
            onClick={() => onSelectMethod('material_form')}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EditNote sx={{ fontSize: 48, color: brand.primary, mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Fill In Material Request Form
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Don't have a BOQ? Tell us what materials you need using our form.
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ mr: 1, color: 'success.main' }}>✓</Box>
                  Select materials from organized categories
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ mr: 1, color: 'success.main' }}>✓</Box>
                  Specify exact quantities and specifications
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box component="span" sx={{ mr: 1, color: 'success.main' }}>✓</Box>
                  Add custom items if needed
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ mr: 1, color: 'success.main' }}>✓</Box>
                  We'll format it properly for suppliers
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Best for: Clients who know what they need but don't have BOQ
              </Typography>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<EditNote />}
                sx={{
                  background: brand.gradient,
                  '&:hover': {
                    background: brand.gradientHover
                  }
                }}
              >
                Fill In Request Form
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Help Section */}
      <Paper elevation={1} sx={{ p: 3, bgcolor: bg.page }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          💡 Not sure what you need?
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={() => {
              // TODO: Download BOQ template
              alert('BOQ template download coming soon');
            }}
          >
            Download BOQ Template
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Help />}
            onClick={() => {
              // TODO: Get help from contractor
              alert('Help from contractor coming soon');
            }}
          >
            Get Help from Contractor
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Chat />}
            onClick={() => {
              // TODO: Chat with support
              alert('Support chat coming soon');
            }}
          >
            Chat with Support
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default GetQuoteMethodSelector;

